import { useState } from "react";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { LegalShell as Layout } from "@/components/connect/LegalShell";
import { Link } from "react-router-dom";
import { Search, Mail, ExternalLink, CreditCard, Shield, AlertCircle, Lock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ContactForm } from "@/components/contact/ContactForm";

/**
 * Truthful posture (May 2026):
 *  - elefa is an OTT VoIP app. Not an MVNO. No SIMs, no eSIMs, no data
 *    plans, no rollover, no coverage map, no "BAL to 1234".
 *  - No staffed phone-support line yet. Support is email + in-app only.
 *  - Cannot reach emergency services.
 */
const faqCategories = [
  {
    name: "Getting started",
    faqs: [
      {
        question: "What is elefa?",
        answer:
          "elefa is a Voice-over-IP (VoIP) app that lets you call any phone number in the world over the internet. Your data or Wi-Fi connection carries the call to our servers and from there to the destination phone. You keep using your normal phone plan for everything else.",
      },
      {
        question: "How do I sign up?",
        answer:
          "Download elefa from the App Store or Google Play, or visit elefa.tel in any modern web browser. Verify your mobile number with a one-time SMS code and you're in. No SIM swap, no carrier activation, no contract.",
      },
      {
        question: "Which devices can I use it on?",
        answer:
          "Any iPhone running iOS 14 or later, any Android phone running Android 8.0 (Oreo) or later, and any modern web browser at elefa.tel. You can be signed in on more than one device at a time.",
      },
    ],
  },
  {
    name: "Calling and rates",
    faqs: [
      {
        question: "How am I charged for calls?",
        answer:
          "elefa is prepaid. You top up wallet credit in advance and each call is billed at the per-minute rate shown for the destination before you dial. There is no monthly bill, no contract and no late fees.",
      },
      {
        question: "Where can I see the rate before I call?",
        answer:
          "Open the dialer, enter the destination number, and the per-minute rate for that destination appears under the number. You can also browse all rates under Rates in the app or at /connect-portal/rates.",
      },
      {
        question: "Does my credit expire?",
        answer:
          "Purchased wallet credit does not expire while your account is active. Promotional credit (signup bonuses, referral rewards) may have an expiry date which is shown at the time it is awarded.",
      },
    ],
  },
  {
    name: "Quality and connection",
    faqs: [
      {
        question: "My call sounds choppy or drops. What helps?",
        answer:
          "VoIP needs a stable internet connection. Try moving to a stronger Wi-Fi signal or a less congested mobile-data area, close other bandwidth-heavy apps, and keep the elefa app updated. If problems continue, email support@elefa.com.au with the call time and destination.",
      },
      {
        question: "Does it work without internet?",
        answer:
          "No. elefa is an internet-only service. If you have no Wi-Fi or mobile data, the app cannot make or receive calls.",
      },
      {
        question: "Can I call emergency services (000, 112, 911)?",
        answer:
          "No. elefa is OTT VoIP and cannot route calls to emergency services anywhere in the world. Always keep a normal mobile or fixed-line phone available for emergencies. See our Terms of Service section 13 for the full safety notice.",
      },
    ],
  },
  {
    name: "Privacy and safety",
    faqs: [
      {
        question: "Is the service available in my country?",
        answer:
          "Several countries restrict or block OTT VoIP, including (but not limited to) the UAE, Saudi Arabia, Oman, Qatar, Kuwait, Morocco, China, Iran, North Korea and Cuba. You are responsible for confirming local law before you use the app. See our Service Availability page for country-by-country guidance.",
      },
      {
        question: "How is my data handled?",
        answer:
          "All traffic between your device and our servers is encrypted in transit. We never sell your data. Full details are in our Privacy Policy.",
      },
    ],
  },
];

const Support = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      faqs: category.faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((category) => category.faqs.length > 0);

  return (
    <Layout>
      <SEOHead
        title={PAGE_SEO.support.title}
        description={PAGE_SEO.support.description}
        keywords={PAGE_SEO.support.keywords}
        canonicalUrl={getCanonicalUrl('/support')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Support', url: 'https://elefa.tel/support' },
        ])}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-elefa-cream via-background to-background py-16">
        <div className="container text-center">
          <h1 className="mb-4 text-4xl font-bold text-foreground sm:text-5xl">
            How can we <span className="text-gradient">help?</span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Search the FAQ or send our team a message. We reply by email within
            two business days (Melbourne time).
          </p>

          <div className="mx-auto mt-8 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact options - email only, no phone line yet */}
      <section className="border-b border-border py-12">
        <div className="container">
          <h2 className="mb-2 text-center text-xl font-semibold text-foreground">
            Contact our teams
          </h2>
          <p className="mx-auto max-w-2xl text-center text-sm text-muted-foreground mb-6">
            We are currently email-only. Pick the address that matches your question.
          </p>
          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <a
              href="mailto:support@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">General support</p>
                <p className="text-sm text-muted-foreground">support@elefa.com.au</p>
              </div>
            </a>
            <a
              href="mailto:billing@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10">
                <CreditCard className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Billing &amp; refunds</p>
                <p className="text-sm text-muted-foreground">billing@elefa.com.au</p>
              </div>
            </a>
            <a
              href="mailto:security@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/20">
                <Shield className="h-5 w-5 text-accent-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground">Security &amp; abuse</p>
                <p className="text-sm text-muted-foreground">security@elefa.com.au</p>
              </div>
            </a>
            <a
              href="mailto:complaints@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
                <AlertCircle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="font-medium text-foreground">Complaints</p>
                <p className="text-sm text-muted-foreground">complaints@elefa.com.au</p>
              </div>
            </a>
            <a
              href="mailto:privacy@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Lock className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground">Privacy enquiries</p>
                <p className="text-sm text-muted-foreground">privacy@elefa.com.au</p>
              </div>
            </a>
            <a
              href="mailto:hardship@elefa.com.au"
              className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-all hover:shadow-card hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Financial difficulty</p>
                <p className="text-sm text-muted-foreground">hardship@elefa.com.au</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
              Frequently asked questions
            </h2>

            {(searchQuery ? filteredCategories : faqCategories).map((category) => (
              <div key={category.name} className="mb-8">
                <h3 className="mb-4 text-lg font-semibold text-foreground">
                  {category.name}
                </h3>
                <Accordion type="single" collapsible className="space-y-2">
                  {category.faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`${category.name}-${index}`}
                      className="rounded-xl border bg-card px-4"
                    >
                      <AccordionTrigger className="text-left hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}

            {searchQuery && filteredCategories.length === 0 && (
              <div className="rounded-xl border bg-muted/30 p-8 text-center">
                <p className="text-muted-foreground">
                  No results found for "{searchQuery}"
                </p>
                <Button variant="link" onClick={() => setSearchQuery("")}>
                  Clear search
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <div className="mb-8 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Send className="h-4 w-4" />
                <span>Contact form</span>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-foreground">
                Send us a message
              </h2>
              <p className="text-muted-foreground">
                Can't find what you're looking for? Send us a message and we'll
                reply within two business days.
              </p>
            </div>

            <ContactForm
              title="Get in touch"
              description="Select a category and tell us how we can help."
              allowedCategories={["general", "billing", "security", "complaints", "privacy"]}
            />
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="py-16">
        <div className="container">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            Quick links
          </h2>

          <div className="mx-auto grid max-w-3xl gap-4 sm:grid-cols-2">
            <Link
              to="/connect-portal/auth"
              className="flex items-center justify-between rounded-xl border bg-card p-4 transition-all hover:shadow-card"
            >
              <span className="font-medium text-foreground">Sign in or create an account</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link
              to="/connect-portal/rates"
              className="flex items-center justify-between rounded-xl border bg-card p-4 transition-all hover:shadow-card"
            >
              <span className="font-medium text-foreground">View calling rates</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link
              to="/legal/service-availability"
              className="flex items-center justify-between rounded-xl border bg-card p-4 transition-all hover:shadow-card"
            >
              <span className="font-medium text-foreground">Service availability by country</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </Link>
            <Link
              to="/legal/financial-hardship"
              className="flex items-center justify-between rounded-xl border bg-card p-4 transition-all hover:shadow-card"
            >
              <span className="font-medium text-foreground">Financial difficulty</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Support;
